/*
 * Step trail animation for sections/countdown.liquid.
 *
 * A dot travels down the connector line and each numbered circle lights up in
 * turn, looping while the list is on screen. Ported from the Lititz redesign
 * reference (js/site.js initStepTrail), with four changes:
 *
 *   - idempotent: this theme emits <script src> tags with no double-load
 *     guard, so the file re-executes when the section appears twice.
 *   - the rAF loop is cancelled when the list leaves the viewport instead of
 *     running forever and merely skipping its updates.
 *   - the cycle restarts from step 1 on re-entry rather than resuming
 *     mid-cycle.
 *   - re-initialises on shopify:section:load, as assets/animations.js does.
 */
(function () {
  'use strict';

  var TRAVEL_MS = 2000;
  var DWELL_MS = 2800;
  var REST_MS = 6000;
  var SEGMENT_MS = TRAVEL_MS + DWELL_MS;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initTrail(list) {
    if (list.dataset.countdownBound === 'true') return;
    list.dataset.countdownBound = 'true';

    var circles = Array.prototype.slice.call(list.querySelectorAll('.countdown__num'));
    var line = list.querySelector('.countdown__line');
    var dot = list.querySelector('.countdown__dot');
    if (!dot || circles.length < 2) return;

    var centers = [];

    function measure() {
      var top = list.getBoundingClientRect().top;
      centers = circles.map(function (el) {
        return el.getBoundingClientRect().top - top + el.offsetHeight / 2;
      });
      if (line && centers.length > 1) {
        /* Terminate the connector exactly at the first and last circle
           centres, replacing the CSS top/bottom fallback. */
        line.style.top = centers[0] + 'px';
        line.style.bottom = 'auto';
        line.style.height = Math.max(0, centers[centers.length - 1] - centers[0]) + 'px';
      }
    }

    function setActive(index) {
      circles.forEach(function (circle, i) {
        circle.classList.toggle('is-active', i === index);
      });
    }

    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(list);
    window.addEventListener('resize', measure);

    list.classList.add('is-ready');
    dot.style.top = (centers[0] || 0) + 'px';

    if (reduceMotion) {
      /* Park the dot on the first step and leave every circle at rest. */
      return;
    }

    var frame = null;
    var start = 0;

    function tick(now) {
      if (centers.length > 1) {
        var total = centers.length * SEGMENT_MS + REST_MS;
        var t = (now - start) % total;

        if (t >= centers.length * SEGMENT_MS) {
          /* Resting: park on the last step with nothing highlighted. */
          dot.style.top = centers[centers.length - 1] + 'px';
          setActive(-1);
        } else {
          var i = Math.floor(t / SEGMENT_MS);
          var local = t - i * SEGMENT_MS;

          if (local < DWELL_MS) {
            dot.style.top = centers[i] + 'px';
            setActive(i);
          } else {
            var p = (local - DWELL_MS) / TRAVEL_MS;
            var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
            var from = centers[i];
            var to = centers[(i + 1) % centers.length];
            dot.style.top = (i === centers.length - 1 ? from : from + (to - from) * eased) + 'px';
            setActive(-1);
          }
        }
      }
      frame = requestAnimationFrame(tick);
    }

    function play() {
      if (frame !== null) return;
      measure();
      /* Reset the clock so re-entering the viewport always starts at step 1. */
      start = performance.now();
      frame = requestAnimationFrame(tick);
    }

    function pause() {
      if (frame === null) return;
      cancelAnimationFrame(frame);
      frame = null;
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          if (entries[0] && entries[0].isIntersecting) play();
          else pause();
        },
        { threshold: 0.25 }
      ).observe(list);
    } else {
      play();
    }
  }

  function init(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll('[data-countdown-trail]');
    Array.prototype.forEach.call(lists, initTrail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
